import isArray from 'lodash/isArray';
import memoize from 'lodash/memoize';

const memoizedConsoleWarn = memoize(console.warn.bind(console));

export default async function* combineAsyncIterables<T>(
    getSortValue: (t: T) => any,
    sortDirection: 'desc' | 'asc',
    ...iterables: (AsyncIterable<T> | Array<T>)[]
): AsyncIterableIterator<T> {
    const iterators = iterables.map((iterable) =>
        isArray(iterable) ? iterable[Symbol.iterator]() : iterable[Symbol.asyncIterator]()
    );
    const results = await Promise.all(iterators.map((iterator) => iterator.next()));
    const prevResults = [];

    while (true) {
        let index = -1;

        for (let i = 0; i < results.length; i++) {
            if (results[i].done) {
                continue;
            } else if (index === -1) {
                index = i;
            } else if (
                compare(results[i].value, results[index].value, getSortValue, sortDirection)
            ) {
                index = i;
            }
        }

        if (index === -1) break;
        yield results[index].value;

        prevResults[index] = results[index];
        results[index] = await iterators[index].next();

        if (
            !results[index].done &&
            compare(results[index].value, prevResults[index].value, getSortValue, sortDirection) &&
            DEPLOYMENT !== 'production'
        ) {
            memoizedConsoleWarn(
                `combineAsyncIterables input is not sorted in "${sortDirection}" order\n` +
                    `${getSortValue(prevResults[index].value)} is followed by ${getSortValue(
                        results[index].value
                    )}\n` +
                    `${getSortValue.toString()}`
            );
        }
    }
}

function compare<T>(a: T, b: T, getSortValue: (t: T) => any, sortDirection: 'desc' | 'asc') {
    const aValue = getSortValue(a);
    const bValue = getSortValue(b);

    if (sortDirection === 'desc') {
        return aValue > bValue;
    } else {
        return aValue < bValue;
    }
}
